import type { GetListParams } from "ra-core";

/**
 * Query building for the dashboard API's echo/session list endpoints
 * (`GET /echoes`, `GET /video-sessions`).
 *
 * The API filters on a fixed set of fields (its `list_index` filter_fields)
 * and returns NO rows when it receives a filter key it does not know, so only
 * the keys below are forwarded; anything else is applied client-side on the
 * page that comes back. `q` is the API's free-text search.
 *
 * Two shapes are built:
 *  - `echoesPath`: filters only, for the collect-everything scan callers that
 *    need more than one API page (analytics, the weekly dashboard);
 *  - `echoPagePath`: one server-side page (limit/offset/sort + filters), for
 *    the list views that only ever show 25–100 rows.
 */

/** Keys of `list_index.filter_fields` for the ECHO / SESSION projections. */
export const ECHO_SERVER_FILTER_FIELDS: ReadonlySet<string> = new Set([
  "id",
  "session_id",
  "source_id",
  "contact_id",
  "whatsapp_user_id",
  "status",
  "source_confidence",
  "poc_collection",
  "rating",
  "display_date",
  "completed_at",
  "started_at",
  "voice_id",
  "echo_origin",
  // Echo content language (LANG-401 API, RU-500 filter): "en" also matches the
  // Echoes with no stored language.
  "content_language",
  // Studio films (B6.2/B6.3): agency, creator (portal user id), made by staff
  // ("true"/"false") and format. ⚠ Deploy the dashboard API that knows them
  // BEFORE this CRM: an API without them answers these filters with no rows.
  "studio_agency_id",
  "studio_created_by",
  "studio_created_by_staff",
  "studio_format",
]);

/**
 * The dashboard-API sentinel for "this row carries no echo_origin" — an
 * organic, customer-made Echo. Test Echoes launched by the autoqueue carry
 * `echo_origin=test_autoqueue`, so `?echo_origin=organic` is how the "Hide
 * test echoes" filter asks the API for real traffic only.
 */
export const ORGANIC_ECHO_ORIGIN = "organic";

const SERVER_OPERATORS = new Set(["eq", "gte", "gt", "lte", "lt", "in"]);

/** The largest page the API serves; bigger asks fall back to a full scan. */
export const ECHO_SERVER_PAGE_MAX = 1000;

export type SplitEchoFilters = {
  /** Forwarded to the API verbatim (values already stringified). */
  server: Record<string, string>;
  /** Kept for `matchesFilter` on the rows that come back. */
  client: Record<string, unknown>;
};

export function splitEchoFilters(
  filter: Record<string, unknown> | undefined,
): SplitEchoFilters {
  const server: Record<string, string> = {};
  const client: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(filter ?? {})) {
    if (raw == null || raw === "") continue;
    const value = serverFilterValue(key, raw);
    if (value == null) {
      client[key] = raw;
    } else {
      server[key] = value;
    }
  }
  return { server, client };
}

/** Full-scan path: filters the API understands, no paging controls. */
export function echoesPath(
  filter: Record<string, unknown> | undefined,
  base = "/echoes",
): string {
  const query = encodePairs(splitEchoFilters(filter).server);
  return query ? `${base}?${query}` : base;
}

/** True when the caller's page fits one API page and can be served directly. */
export function wantsServerPage(params: Partial<GetListParams>): boolean {
  const perPage = params.pagination?.perPage;
  return perPage != null && perPage > 0 && perPage < ECHO_SERVER_PAGE_MAX;
}

/** One list page: limit/offset/sort plus the server-side filters. */
export function echoPagePath(
  base: string,
  params: Partial<GetListParams>,
  server: Record<string, string>,
): string {
  const page = Math.max(1, params.pagination?.page ?? 1);
  const perPage = Math.max(1, params.pagination?.perPage ?? 25);
  const pairs: Record<string, string> = {
    limit: String(perPage),
    offset: String((page - 1) * perPage),
  };
  if (params.sort?.field) {
    pairs.sort = params.sort.field;
    pairs.order = params.sort.order === "ASC" ? "ASC" : "DESC";
  }
  return `${base}?${encodePairs({ ...pairs, ...server })}`;
}

function serverFilterValue(key: string, raw: unknown): string | null {
  if (key === "q") return stringValue(raw);
  const at = key.indexOf("@");
  const field = at === -1 ? key : key.slice(0, at);
  const operator = at === -1 ? "eq" : key.slice(at + 1);
  if (
    !ECHO_SERVER_FILTER_FIELDS.has(field) ||
    !SERVER_OPERATORS.has(operator)
  ) {
    return null;
  }
  if (Array.isArray(raw)) {
    const items = raw.map((item) => String(item).trim()).filter(Boolean);
    return items.length ? items.join(",") : null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "boolean") return String(raw);
  return stringValue(raw);
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function encodePairs(pairs: Record<string, string>): string {
  return Object.entries(pairs)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
}
